import { API_BASE_URL, WS_URL } from '../config';
import { rsaEncrypt, rsaDecrypt, generateNonce, generateDynamicSignature, hmacSha256 } from './crypto';

// API基础配置
const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'X-Encrypted': 'true'
  }
};

// JWT过期检测
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    // 解析JWT token
    const payload = JSON.parse(atob(token.split('.')[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    // 检查是否过期（提前5分钟刷新）
    return payload.exp - currentTime < 300;
  } catch (error) {
    return true;
  }
};

// 自动重新认证
let reAuthPromise = null;
const reAuthenticate = async (userKeys) => {
  if (reAuthPromise) {
    return reAuthPromise;
  }
  
  reAuthPromise = (async () => {
    try {
      
      // 1. 请求挑战
      const encryptedUserId = rsaEncrypt(userKeys.userId, userKeys.serverPublicKey);
      if (!encryptedUserId) {
        throw new Error('加密用户ID失败');
      }

      const challengeResponse = await authAPI.requestChallenge(encryptedUserId);
      if (!challengeResponse.success) {
        throw new Error(challengeResponse.message || '请求挑战失败');
      }

      const { encrypted_challenge, challenge_id } = challengeResponse.data;

      // 2. 解密挑战
      const challenge = rsaDecrypt(encrypted_challenge, userKeys.privateKey);
      if (!challenge) {
        throw new Error('解密挑战失败');
      }

      // 3. 加密响应
      const encryptedResponse = rsaEncrypt(challenge, userKeys.serverPublicKey);
      if (!encryptedResponse) {
        throw new Error('加密响应失败');
      }

      // 4. 验证挑战
      const verifyResponse = await authAPI.verifyChallenge(encryptedUserId, encryptedResponse, challenge_id);
      if (!verifyResponse.success) {
        throw new Error(verifyResponse.message || '验证挑战失败');
      }

      const { access_token, refresh_token, encrypted_user_key } = verifyResponse.data;

      // 5. 更新用户密钥
      const updatedUserKeys = {
        ...userKeys,
        accessToken: access_token,
        refreshToken: refresh_token,
        userSecretKey: encrypted_user_key
      };

      // 6. 保存到本地存储
      localStorage.setItem('toxchat_keys', JSON.stringify(updatedUserKeys));
      
      return updatedUserKeys;
    } catch (error) {
      throw error;
    } finally {
      reAuthPromise = null;
    }
  })();
  
  return reAuthPromise;
};

// 通用API请求函数
const apiRequest = async (endpoint, options = {}, userKeys = null) => {
  const url = `${API_CONFIG.baseURL}${endpoint}`;
  
  // 如果需要动态签名
  if (userKeys && userKeys.accessToken && userKeys.userSecretKey) {
    // 检查Token是否过期
    if (isTokenExpired(userKeys.accessToken)) {
      try {
        const updatedUserKeys = await reAuthenticate(userKeys);
        // 更新传入的userKeys
        Object.assign(userKeys, updatedUserKeys);
      } catch (error) {
        throw new Error('认证已过期，请重新登录');
      }
    }
    
    const timestamp = Math.floor(Date.now() / 1000); // 使用UTC时间戳
    const nonce = generateNonce();
    
    // 构建请求数据
    let requestData = {};
    if (options.body) {
      // POST/PUT请求，从请求体获取数据
      requestData = JSON.parse(options.body);
    } else {
      // GET请求，从URL查询参数获取数据
      const urlObj = new URL(url, window.location.origin);
      requestData = Object.fromEntries(urlObj.searchParams);
    }
    
    
    const signature = generateDynamicSignature(
      userKeys.accessToken,
      timestamp.toString(),
      nonce,
      requestData,
      userKeys.userSecretKey,
      userKeys.privateKey
    );
    
    
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${userKeys.accessToken}`,
      'X-Timestamp': timestamp.toString(),
      'X-Nonce': nonce,
      'X-Signature': signature
    };
  }
  
  const config = {
    ...API_CONFIG,
    ...options,
    headers: {
      ...API_CONFIG.headers,
      ...options.headers
    }
  };

  try {
    const response = await fetch(url, config);
    
    const text = await response.text();
    
    // 检查响应是否为空
    if (!text || text.trim() === '') {
      throw new Error('API响应为空');
    }
    
    let data;
    try {
      data = JSON.parse(text);
    } catch (parseError) {
      throw new Error(`JSON解析失败: ${parseError.message}`);
    }
    
    if (!response.ok) {
      // 如果是401未授权错误，尝试自动重新认证
      if (response.status === 401 && userKeys) {
        try {
          const updatedUserKeys = await reAuthenticate(userKeys);
          Object.assign(userKeys, updatedUserKeys);
          
          // 重新发送请求
          return await apiRequest(endpoint, options, userKeys);
        } catch (reauthError) {
          throw new Error('认证已过期，请重新登录');
        }
      }
      
      throw new Error(data.error || `HTTP ${response.status}`);
    }
    
    return data;
  } catch (error) {
    throw error;
  }
};

// 加密请求数据
const encryptRequestData = (data, serverPublicKey) => {
  const jsonData = JSON.stringify(data);
  const encrypted = rsaEncrypt(jsonData, serverPublicKey);
  return encrypted;
};

// 解密响应数据
const decryptResponseData = (encryptedData, userPrivateKey) => {
  return rsaDecrypt(encryptedData, userPrivateKey);
};

// 认证相关API
export const authAPI = {
  // 获取服务器公钥
  getServerPublicKey: async () => {
    return await apiRequest('/api/v1/auth/public-key');
  },

  // 用户注册
  register: async (publicKey) => {
    return await apiRequest('/api/v1/auth/register', {
      method: 'POST',
      body: JSON.stringify({ 
        public_key: publicKey
      })
    });
  },

  // 请求挑战
  requestChallenge: async (encryptedUserId) => {
    return await apiRequest('/api/v1/auth/challenge', {
      method: 'POST',
      body: JSON.stringify({ 
        encrypted_user_id: encryptedUserId
      })
    });
  },

  // 验证挑战
  verifyChallenge: async (encryptedUserId, encryptedResponse, challengeId) => {
    return await apiRequest('/api/v1/auth/verify', {
      method: 'POST',
      body: JSON.stringify({ 
        encrypted_user_id: encryptedUserId,
        encrypted_response: encryptedResponse,
        challenge_id: challengeId
      })
    });
  },

  // 用户登出
  logout: async (userKeys) => {
    return await apiRequest('/api/v1/auth/logout', {
      method: 'POST'
    }, userKeys);
  },

  // 获取用户信息
  getUserInfo: async (userKeys) => {
    return await apiRequest('/api/v1/auth/user-info', {
      method: 'GET'
    }, userKeys);
  },

  // 获取在线用户列表
  getOnlineUsers: async (userKeys) => {
    return await apiRequest('/api/v1/auth/online-users', {
      method: 'GET'
    }, userKeys);
  }
};

// 公共聊天API
export const publicChatAPI = {
  // 发送公共消息
  sendMessage: async (messageData, userKeys) => {
    // 只加密消息内容，而不是整个对象
    const encryptedMessage = rsaEncrypt(messageData.encrypted_message, userKeys.serverPublicKey);
    if (!encryptedMessage) {
      throw new Error('消息加密失败');
    }
    return await apiRequest('/api/v1/public/message', {
      method: 'POST',
      body: JSON.stringify({
        encrypted_message: encryptedMessage,
        user_id: messageData.user_id,
        timestamp: messageData.timestamp,
        is_custom_aes: messageData.is_custom_aes || false,
        custom_tag: messageData.custom_tag || ''
      })
    }, userKeys);
  },

  // 获取公共消息
  getMessages: async (userId, lastMessageId = 0, limit = 50, userKeys) => {
    const params = new URLSearchParams({
      user_id: userId,
      last_message_id: lastMessageId.toString(),
      limit: limit.toString()
    });
    return await apiRequest(`/api/v1/public/messages?${params.toString()}`, {
      method: 'GET'
    }, userKeys);
  }
};

// 私聊API
export const privateChatAPI = {
  // 创建私聊会话
  createChat: async (fromUserId, toUserId, encryptedMessage) => {
    return await apiRequest('/api/v1/private/chat', {
      method: 'POST',
      body: JSON.stringify({
        from_user_id: fromUserId,
        to_user_id: toUserId,
        encrypted_message: encryptedMessage
      })
    });
  },

  // 发送私聊消息
  sendMessage: async (chatId, encryptedMessage, fromUserId) => {
    return await apiRequest('/api/v1/private/message', {
      method: 'POST',
      body: JSON.stringify({
        chat_id: chatId,
        encrypted_message: encryptedMessage,
        from_user_id: fromUserId
      })
    });
  },

  // 获取私聊消息
  getMessages: async (chatId, userId, lastMessageId = 0, limit = 50) => {
    return await apiRequest('/api/v1/private/messages', {
      method: 'POST',
      body: JSON.stringify({
        chat_id: chatId,
        user_id: userId,
        last_message_id: lastMessageId,
        limit: limit
      })
    });
  },

  // 标记消息为已读
  markMessagesRead: async (chatId, messageIds, userId) => {
    return await apiRequest('/api/v1/private/messages/read', {
      method: 'PUT',
      body: JSON.stringify({
        chat_id: chatId,
        message_ids: messageIds,
        user_id: userId
      })
    });
  },

  // 获取用户公钥
  getUserPublicKey: async (userId) => {
    return await apiRequest(`/api/v1/private/user/${userId}/public-key`);
  }
};

// 笔记API
export const notesAPI = {
  // 创建笔记
  createNote: async (encryptedContent, title, userId) => {
    return await apiRequest('/api/v1/notes', {
      method: 'POST',
      body: JSON.stringify({
        encrypted_content: encryptedContent,
        title: title,
        user_id: userId
      })
    });
  },

  // 获取笔记列表
  getNotes: async (userId) => {
    return await apiRequest('/api/v1/notes', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    });
  },

  // 更新笔记
  updateNote: async (noteId, encryptedContent, title, userId) => {
    return await apiRequest('/api/v1/notes', {
      method: 'PUT',
      body: JSON.stringify({
        note_id: noteId,
        encrypted_content: encryptedContent,
        title: title,
        user_id: userId
      })
    });
  },

  // 删除笔记
  deleteNote: async (noteId, userId) => {
    return await apiRequest('/api/v1/notes', {
      method: 'DELETE',
      body: JSON.stringify({
        note_id: noteId,
        user_id: userId
      })
    });
  },

  // 搜索笔记
  searchNotes: async (userId, keyword) => {
    return await apiRequest(`/api/v1/notes/search?keyword=${encodeURIComponent(keyword)}&user_id=${userId}`);
  },

  // 获取笔记数量
  getNoteCount: async (userId) => {
    return await apiRequest(`/api/v1/notes/count?user_id=${userId}`);
  },

  // 获取单个笔记
  getNote: async (noteId, userId) => {
    return await apiRequest(`/api/v1/notes/${noteId}?user_id=${userId}`);
  }
};

// WebSocket连接
export const createWebSocketConnection = (userKeys, onMessage, onError, onClose) => {
  if (!userKeys || !userKeys.accessToken) {
    throw new Error('缺少认证信息');
  }
  
  const ws = new WebSocket(`${WS_URL}?token=${userKeys.accessToken}`);
  
  ws.onopen = () => {
  };
  
  ws.onmessage = (event) => {
    try {
      const message = JSON.parse(event.data);
      onMessage(message);
    } catch (error) {
    }
  };
  
  ws.onerror = (error) => {
    onError(error);
  };
  
  ws.onclose = () => {
    onClose();
  };
  
  return ws;
};

// 发送带认证的WebSocket消息
export const sendAuthenticatedWSMessage = (ws, type, data, userKeys) => {
  if (!userKeys || !userKeys.userSecretKey) {
    throw new Error('缺少用户密钥');
  }
  
  const timestamp = Math.floor(Date.now() / 1000);
  const nonce = generateNonce();
  const signatureData = `${type}.${timestamp}.${nonce}.${JSON.stringify(data)}`;
  const signature = hmacSha256(signatureData, userKeys.userSecretKey);
  
  const message = {
    type: type,
    data: data,
    timestamp: timestamp,
    nonce: nonce,
    signature: signature
  };
  
  ws.send(JSON.stringify(message));
};

// 健康检查
export const healthCheck = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return await response.json();
  } catch (error) {
    throw error;
  }
};

// 导出自动重新认证函数
export const autoReAuthenticate = reAuthenticate;

// 导出Token过期检测函数
export const checkTokenExpiry = isTokenExpired;

// 导出通用API请求函数
export { apiRequest };

// 导出所有API
export default {
  auth: authAPI,
  publicChat: publicChatAPI,
  privateChat: privateChatAPI,
  notes: notesAPI,
  createWebSocketConnection,
  healthCheck,
  autoReAuthenticate,
  checkTokenExpiry
};
